// AM Advertising Home — the best-performing-Meta-creatives ranking. PURE.
//
// ⚠️ The SAME volume discipline as every CPL ranking on this page: an ad with
// 1 lead at $4 must NOT top the gallery, so only ads clearing BOTH floors
// (leads + spend) rank at all. The server's candidate query already orders by
// CPL, but the floors live HERE (pure, mutation-checked) — the candidates
// deliberately include below-floor ads so this guard is the deciding gate.

import type { HomeCreative } from '../../types/advertisingHome.js';
import { classifyCpl, compareVerdictsWorstFirst, type BrandAverages } from './noLeads.js';
import { CREATIVE_MIN_LEADS, CREATIVE_MIN_SPEND, CREATIVE_TOP_N } from './guards.js';

export function creativeCpl(row: HomeCreative): number | null {
  if (row.leads <= 0) return null;
  return row.spend / row.leads;
}

/** Lowest-CPL first among volume-qualified ads; ties → more leads first. */
export function rankBestCreatives(
  rows: readonly HomeCreative[],
  topN = CREATIVE_TOP_N,
): HomeCreative[] {
  return rows
    .filter((r) => r.leads >= CREATIVE_MIN_LEADS && r.spend >= CREATIVE_MIN_SPEND)
    .map((r) => ({ row: r, cpl: creativeCpl(r) }))
    .filter((x): x is { row: HomeCreative; cpl: number } => x.cpl !== null)
    .sort((a, b) => a.cpl - b.cpl || b.row.leads - a.row.leads)
    .slice(0, topN)
    .map((x) => x.row);
}

/**
 * Ads SPENDING WITH NO LEADS — the rows `rankBestCreatives` can never contain.
 * Judged by the ONE rule (noLeads.ts): spend at or above the brand's average
 * CPL (Meta, this currency, this window) or the fallback. Worst (most spend)
 * first; none is ever shown with a CPL.
 */
export function rankSpendingNoLeadCreatives(
  rows: readonly HomeCreative[],
  brandOfAccount: (accountId: string) => string | null,
  brandAverages: BrandAverages,
  topN = CREATIVE_TOP_N,
): HomeCreative[] {
  return rows
    .map((row) => ({
      row,
      verdict: classifyCpl({
        spend: row.spend,
        leads: row.leads,
        brandCpl: brandAverages.cpl(brandOfAccount(row.accountId), 'meta', row.currency),
      }),
    }))
    .filter((x) => x.verdict.kind === 'no_leads')
    .sort((a, b) => compareVerdictsWorstFirst(a.verdict, b.verdict))
    .slice(0, topN)
    .map((x) => x.row);
}
